import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { INCOMMODITY_SATISFACTION_MANAGER } from "../../../../consts/incommodity";

import {
  calculateTimeDifference,
  convertFullDateTimeToWithoutSecond,
  formatFullDateTime,
  getYearToMinuteFromISODateString,
} from "../../../../libs/dateFormat";

import { useUserContext } from "../../../../contexts/userContext";

import {
  REPORT_STATUS,
  ReportDetailItem,
  getManagerReportDetail,
  completeManagerReport,
  modifyManagerReport,
  scheduleManagerReport,
  workingManagerReport,
  ZadaraFile,
  S3File,
} from "../../../../services/mangerReportServices";
import { AtgResource, AtgWorker } from "../../../../services/atgServices";
import {
  SatisfactionRequest,
  getIncommodityVirtualNumber,
} from "../../../../services/userReportServices";

import useFullScreenLoading from "../../../../hooks/useFullScreenLoading";
import useSimplePopup from "../../../../hooks/useSimplePopup";
import useVirtualNumber from "../../../../hooks/useVirtualNumber";

import CommonButton from "../../../../components/Button";
import StickyHeader from "../../../../components/Layout/StickyHeader";
import ProgressBar from "../../../../components/ProgressBar";
import HorizontalRule from "../../../../components/Rule/Horizontal";
import CommonTextArea from "../../../../components/TextArea";
import CommonPopup from "../../../../components/Popup";
import CommonInput from "../../../../components/Input/CommonInput";
import CommonImageInput from "../../../../components/Input/ImageUpload";
import FindWorker from "../../../../components/Find/Worker";
import FindResource from "../../../../components/Find/Resource";
import ManagerReportFeedbackRecommend from "../../../../components/Manager/Report/FeedbackRecommend";
import ImagePreview from "../../../../components/Input/ImagePreview";
import HistorySatisfaction from "../../../../components/History/Satisfaction";
import BottomGroup from "../../../../components/Button/Group";
import ManagerReportBeforeAfterImage from "../../../../components/Manager/Report/BeforeAfterImage";
import HistoryDetailRow from "../../../../components/History/DetailRow";
import HistoryDetailBlock from "../../../../components/History/DetailBlock";
import CommonProfile from "../../../../components/MyInfo/Profile";

type Props = {};

export default function ManagerReportDetail(props: Props) {
  const params = useParams();
  const navigate = useNavigate();
  const [user] = useUserContext();
  const [originReportDetail, setOriginReportDetail] =
    React.useState<ReportDetailItem>();
  const [reportDetail, setReportDetail] = React.useState<ReportDetailItem>();
  const [popup, setPopup] = React.useState<
    | "processConfirm"
    | "selectWorker"
    | "selectResource"
    | "scheduledConfirm"
    | "completeConfirm"
    | "modifyConfirm"
    | ""
  >("");
  const [beforeImages, setBeforeImages] = React.useState<(File | string)[]>([]);
  const [afterImages, setAfterImages] = React.useState<(File | string)[]>([]);
  const [worker, setWorker] = React.useState(user.name ?? "");
  const [resource, setResource] = useState("");
  const [atgWorker, setAtgWorker] = React.useState<AtgWorker[]>(
    [{ workerName: user.name } as AtgWorker] ?? []
  );
  const [atgResource, setAtgResource] = React.useState<AtgResource[]>([]);
  const [feedback, setFeedback] = useState<string>("");
  const [editMode, setEditMode] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const [virtualNumberHandler, virtualNumberComponent] = useVirtualNumber({
    getVirtualNumber: getIncommodityVirtualNumber,
  });

  const loadManagerReportDetail = async () => {
    try {
      const res = await getManagerReportDetail({
        centerIncommodityId: Number(params.id),
      });
      if (res.status === HttpStatusCode.Ok) {
        setOriginReportDetail(res.data);
        await setReportDetail(res.data);
        setFeedback(res.data.feedback ?? "");
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("불편신고 상세 조회에 실패했습니다.");
      }
    }
  };

  const getStatusStep = () => {
    switch (reportDetail?.status) {
      case REPORT_STATUS.REQUESTED.toString():
        return 1;
      case REPORT_STATUS.PROCESSING.toString():
      case REPORT_STATUS.SCHEDULED.toString():
        return 2;
      case REPORT_STATUS.COMPLETED.toString():
      case REPORT_STATUS.COMPLETED2.toString():
        return 3;
      case REPORT_STATUS.CANCELED.toString():
      case REPORT_STATUS.CANCELED2.toString():
      default:
        return 0;
    }
  };

  const validateWorkerResource = () => {
    if (!feedback || feedback.length === 0) {
      open("피드백을 입력해주세요.");
      return false;
    }

    if (
      reportDetail?.pushTypeCode?.includes("FACILITY") &&
      reportDetail?.vocId
    ) {
      if (atgWorker.length === 0) {
        open("작업자를 입력해주세요");
        return false;
      }
    } else {
      if (!worker || worker.length === 0) {
        open("작업자를 입력해주세요");
        return false;
      }
    }

    return true;
  };

  const getStatusBottomButton = () => {
    switch (reportDetail?.status) {
      case REPORT_STATUS.REQUESTED.toString():
        return (
          <div className="bg-white flex">
            <CommonButton
              className="w-full rounded-none text-sm px-4 py-3 h-bottom-button"
              onClick={() => {
                setPopup("processConfirm");
              }}
            >
              접수 확인
            </CommonButton>
          </div>
        );
      case REPORT_STATUS.PROCESSING.toString():
      case REPORT_STATUS.SCHEDULED.toString():
        return (
          <div className="flex bg-white">
            {!reportDetail.isScheduled && (
              <CommonButton
                variant="secondary"
                className="w-full rounded-none text-sm px-4 py-3 h-bottom-button"
                onClick={async () => {
                  if (!feedback || feedback.length === 0) {
                    open("피드백을 입력해주세요.");
                    return;
                  }

                  setPopup("scheduledConfirm");
                }}
              >
                처리 예정
              </CommonButton>
            )}
            <CommonButton
              className="w-full rounded-none text-sm px-4 py-3 h-bottom-button"
              onClick={() => {
                if (!validateWorkerResource()) {
                  return;
                }

                setPopup("completeConfirm");
              }}
            >
              처리 완료
            </CommonButton>
          </div>
        );
      case REPORT_STATUS.COMPLETED.toString():
      case REPORT_STATUS.COMPLETED2.toString():
        if (editMode === false) {
          return (
            <div className="bg-white flex">
              <CommonButton
                variant="secondary"
                className="w-full rounded-none text-sm px-4 py-3 h-bottom-button"
                onClick={() => {
                  setEditMode(true);
                }}
              >
                수정
              </CommonButton>
            </div>
          );
        } else {
          return (
            <div className="bg-white flex">
              <CommonButton
                variant="secondary"
                className="w-full rounded-none text-sm px-4 py-3 h-bottom-button"
                onClick={async () => {
                  setReportDetail(originReportDetail);
                  setEditMode(false);
                }}
              >
                취소
              </CommonButton>
              <CommonButton
                className="w-full rounded-none text-sm px-4 py-3 h-bottom-button"
                onClick={() => {
                  if (!validateWorkerResource()) {
                    return;
                  }

                  setPopup("modifyConfirm");
                }}
              >
                수정
              </CommonButton>
            </div>
          );
        }
      default:
        return null;
    }
  };

  useEffect(() => {
    if (
      reportDetail?.s3PostFile?.length &&
      reportDetail?.s3PostFile?.length > 0
    ) {
      setAfterImages(reportDetail?.s3PostFile.map((item) => item.filePath));
    } else {
      setAfterImages([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDetail?.s3PostFile]);

  useEffect(() => {
    if (
      reportDetail?.s3PreFile?.length &&
      reportDetail?.s3PreFile?.length > 0
    ) {
      setBeforeImages(reportDetail?.s3PreFile.map((item) => item.filePath));
    } else {
      setBeforeImages([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDetail?.s3PreFile]);

  useEffect(() => {
    setEditMode(
      [
        REPORT_STATUS.COMPLETED.toString(),
        REPORT_STATUS.COMPLETED2.toString(),
      ].includes(reportDetail?.status ?? "")
        ? false
        : true
    );
  }, [reportDetail]);

  useEffect(() => {
    setAtgWorker([{ workerName: user.name } as AtgWorker] ?? []);
  }, [user.name]);

  useEffect(() => {
    if (user.isLoading) return;
    setIsLoading(true);
    loadManagerReportDetail();
    setIsLoading(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id, user.isLoading]);

  return (
    <div>
      {loadingComponent}
      <StickyHeader
        title="불편신고 상세"
        onBackClick={() => navigate("/manager/voc-manager")}
        className="px-5"
      />
      <div className="flex flex-col gap-2.5 p-5">
        <div className="text-3xl ">{reportDetail?.smallCategory}</div>
        <div className="text-base font-normal text-brand-neutral-40">
          {reportDetail?.largeCategory}
        </div>
      </div>
      <div className="flex flex-col gap-2.5 pt-0 px-5 pb-[30px]">
        <div
          className="p-5 flex flex-col gap-2.5 rounded-[10px]"
          style={{ boxShadow: "0px 1px 5px 0px rgba(0, 0, 0, 0.10)" }}
        >
          <div className=" bg-brand-floating-primary-button bg-clip-text text-base text-transparent font-semibold">
            {reportDetail?.statusName}
          </div>
          <ProgressBar
            currentStep={getStatusStep()}
            wrapperClassName="!p-0"
            className="h-2.5 !bg-brand-floating-primary-button"
            rounded
            nonSticky
          />
          {reportDetail?.feedback && (
            <>
              <HorizontalRule className="!mt-[5px] !mb-[5px]" />
              <div className="flex gap-2.5 items-center">
                <CommonProfile src={reportDetail?.completedByThumbnail} />
                <div className="flex flex-col">
                  <div className="text-sm font-semibold">
                    {reportDetail.completedByName}
                  </div>
                  <div className=" text-xs text-brand-neutral-30">
                    {reportDetail.completedAt}
                  </div>
                </div>
              </div>
              <HorizontalRule className="!mt-[5px] !mb-[5px]" />
              <div className=" text-sm font-bold">{reportDetail.feedback}</div>
              <ManagerReportBeforeAfterImage reportDetail={reportDetail} />
              <HorizontalRule className="!mt-[5px] !mb-[5px]" />
              <div className="py-2.5 flex flex-col gap-2.5 items-center">
                {reportDetail.satisfactionId ? (
                  <HistorySatisfaction
                    satisfaction={{
                      id: reportDetail.satisfactionId,
                      score: reportDetail.satisfaction,
                      comment: reportDetail.satisfactionComment,
                      createdAt: reportDetail.satisfactionCreatedAt,
                      createdBy: reportDetail.satisfactionCreatedBy,
                      createdByName: reportDetail.satisfactionCreatedByName,
                      createdByThumbnail:
                        reportDetail.satisfactionCreatedByThumbnail,
                      reply:
                        reportDetail.satisfactionReplyId &&
                        reportDetail.satisfactionReplyId > 0
                          ? {
                              id: reportDetail.satisfactionReplyId,
                              comment: reportDetail.satisfactionReplyComment,
                              createdAt:
                                reportDetail.satisfactionReplyCreatedAt,
                              createdBy:
                                reportDetail.satisfactionReplyCreatedBy,
                              createdByName:
                                INCOMMODITY_SATISFACTION_MANAGER.NAME,
                              createdByThumbnail:
                                reportDetail.satisfactionReplyCreatedByThumbnail,
                            }
                          : undefined,
                    }}
                    isManager={reportDetail.completedBy === user.memberId}
                    serviceId={reportDetail.centerIncommodityId.toString()}
                    type="incommodity"
                    onLoad={loadManagerReportDetail}
                    postSatisfaction={async (
                      satisfactionId: number,
                      satisfaction: SatisfactionRequest
                    ) => {
                      return;
                    }}
                  />
                ) : (
                  <>
                    <ManagerReportDetailCommentIcon />
                    <div className=" text-brand-neutral-60 text-sm font-bold">
                      등록된 평가가 없어요
                    </div>
                  </>
                )}
              </div>
            </>
          )}
          {!reportDetail?.feedback && (
            <>
              <HorizontalRule className="!mt-[5px] !mb-[5px]" />
              <div className="flex gap-2.5 justify-between items-center">
                <div className="flex flex-col">
                  <div>{reportDetail?.createdByName}</div>
                  <div className="text-xs text-brand-neutral-30">
                    {reportDetail?.createdByCompany}
                  </div>
                </div>
                <CommonButton
                  variant="tertiary"
                  className="h-[34px] px-4 py-[11px] flex items-center justify-center text-sm font-medium leading-[17px] text-brand-neutral-60 whitespace-nowrap design_min:w-[120px]"
                  onClick={() => {
                    if (reportDetail?.createdBy) {
                      virtualNumberHandler(reportDetail?.centerIncommodityId);
                    } else {
                      open(
                        "신청자 정보가 없어서, 안심 번호를 불러오는데 실패했습니다."
                      );
                    }
                  }}
                >
                  신청자 연결
                </CommonButton>
              </div>
            </>
          )}
        </div>

        {reportDetail?.feedback && (
          <div
            className="p-5 flex flex-col gap-2.5 rounded-[10px]"
            style={{ boxShadow: "0px 1px 5px 0px rgba(0, 0, 0, 0.10)" }}
          >
            <div className="flex gap-2.5 justify-between items-center">
              <div className="flex flex-col">
                <div>{reportDetail?.createdByName}</div>
                <div className="text-xs text-brand-neutral-30">
                  {reportDetail?.createdByCompany}
                </div>
              </div>
              <CommonButton
                variant="tertiary"
                className="h-[34px] px-4 py-[11px] flex items-center justify-center text-sm font-medium leading-[17px] text-brand-neutral-60 whitespace-nowrap design_min:w-[120px]"
                onClick={() => {
                  if (reportDetail?.createdBy) {
                    virtualNumberHandler(reportDetail?.centerIncommodityId);
                  } else {
                    open(
                      "신청자 정보가 없어서, 안심 번호를 불러오는데 실패했습니다."
                    );
                  }
                }}
              >
                신청자 연결
              </CommonButton>
            </div>
          </div>
        )}
      </div>

      <div className="px-5">
        <HorizontalRule className="bg-black" margin="0" />
      </div>

      <div className="px-5 flex flex-col gap-10">
        <HistoryDetailBlock title="접수 내용">
          <HistoryDetailRow title="분류">
            <div className="font-bold">{reportDetail?.largeCategory}</div>
            {reportDetail?.middleCategory && (
              <div className="bg-brand-category_tag rounded py-1 pl-1.5 pr-2 flex items-center gap-1.5 text-sm w-fit">
                <div className="w-1 h-1 bg-white rounded-full"></div>
                {reportDetail?.middleCategory}
              </div>
            )}
            <div>{reportDetail?.smallCategory}</div>
          </HistoryDetailRow>

          <HistoryDetailRow title="접수 시간">
            {reportDetail?.createdAt &&
              getYearToMinuteFromISODateString(reportDetail?.createdAt)}
          </HistoryDetailRow>

          <HistoryDetailRow title="접수 번호">
            <strong>{reportDetail?.centerIncommodityId}</strong>{" "}
            {reportDetail?.channel ? `/ ${reportDetail?.channelName}` : ""}
          </HistoryDetailRow>

          <HistoryDetailRow title="접수 위치">
            <div>
              {reportDetail?.buildingName} {reportDetail?.floorName} /{" "}
              {reportDetail?.createdByCompany}
            </div>
            <div>{reportDetail?.location}</div>
          </HistoryDetailRow>

          <HistoryDetailRow title="상세 내용">
            <div>{reportDetail?.categoryName}</div>
            <div>{reportDetail?.description}</div>
          </HistoryDetailRow>

          <HistoryDetailRow title="처리 희망시간">
            {!reportDetail?.deadLine
              ? "즉시 신청"
              : convertFullDateTimeToWithoutSecond(reportDetail.deadLine)}
          </HistoryDetailRow>

          {reportDetail?.userFile?.filePath && (
            <HistoryDetailRow title="접수 사진">
              <ImagePreview
                className="max-h-[calc((100vw-20px)/3)] max-w-[calc((100vw-20px)/3)]"
                image={reportDetail?.userFile?.filePath}
                imageList={[reportDetail?.userFile?.filePath]}
                index={0}
                wrapperClassName="max-w-[calc((100vw-20px)/3)]"
              />
            </HistoryDetailRow>
          )}
        </HistoryDetailBlock>

        {Number(reportDetail?.status) >= REPORT_STATUS.PROCESSING &&
          reportDetail?.receiptedAt && (
            <HistoryDetailBlock title="접수 확인">
              <HistoryDetailRow title="접수 확인 시간">
                {convertFullDateTimeToWithoutSecond(reportDetail.receiptedAt)}
              </HistoryDetailRow>
              <HistoryDetailRow title="확인자">
                {reportDetail.receiptedByName}
              </HistoryDetailRow>
              {reportDetail.completedAt && (
                <>
                  <HistoryDetailRow title="처리 완료 시간">
                    <div className="text-[#194BDC]">
                      {convertFullDateTimeToWithoutSecond(
                        reportDetail.completedAt
                      )}
                    </div>
                    <div>
                      (
                      {calculateTimeDifference(
                        reportDetail.receiptedAt,
                        reportDetail.completedAt
                      )}{" "}
                      소요)
                    </div>
                  </HistoryDetailRow>
                </>
              )}
            </HistoryDetailBlock>
          )}

        {REPORT_STATUS.PROCESSING.toString() === reportDetail?.status && (
          <>
            <HistoryDetailBlock title="작업 내역">
              <HorizontalRule margin="0" />
              <div className="flex flex-col gap-5">
                <div>
                  <div className="py-2.5 text-sm text-brand-neutral-60">
                    작업자<span className="text-red-500">*</span>
                  </div>
                  {reportDetail.pushTypeCode?.includes("FACILITY") &&
                  reportDetail.vocId ? (
                    <>
                      <div className="flex flex-col">
                        <CommonButton
                          variant="tertiary"
                          onClick={() => setPopup("selectWorker")}
                        >
                          작업자 추가
                        </CommonButton>
                        {atgWorker.map((worker, index) => (
                          <React.Fragment key={index}>
                            {index > 0 && <HorizontalRule margin="0" />}
                            <div className="py-1.5 flex gap-2.5 items-center justify-between">
                              <div className="p-2.5 flex gap-1 items-center ">
                                {worker.workerName}{" "}
                                {worker.job && <>({worker.job})</>}
                                {worker.workerName === user.name && (
                                  <div className=" rounded px-1.5 py-0.5 bg-brand-me-badge text-xs text-white">
                                    본인
                                  </div>
                                )}
                              </div>
                              <div
                                onClick={() =>
                                  setAtgWorker((prev) =>
                                    prev.filter(
                                      (item, currentIndex) =>
                                        currentIndex !== index
                                    )
                                  )
                                }
                              >
                                <ManagerReportDetailRemoveIcon />
                              </div>
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                    </>
                  ) : (
                    <CommonInput
                      value={worker}
                      onChange={(text) => setWorker(text)}
                      placeholder="작업자의 이름을 입력해주세요"
                    />
                  )}

                  <div>
                    <div className="py-2.5 text-sm text-brand-neutral-60">
                      투입자재
                    </div>
                    <div className="flex flex-col">
                      {reportDetail.pushTypeCode?.includes("FACILITY") &&
                      reportDetail.vocId ? (
                        <>
                          <CommonButton
                            variant="tertiary"
                            onClick={() => setPopup("selectResource")}
                          >
                            투입자재 추가
                          </CommonButton>
                          {atgResource.map((resource, index) => (
                            <div key={resource.materialCode}>
                              {index > 0 && <HorizontalRule margin="0" />}
                              <div className="py-1.5 flex gap-2.5 items-center justify-between">
                                <div className="p-2.5 flex gap-1 items-center flex-1">
                                  <div className="flex flex-col flex-1 p-4">
                                    <div className=" font-semibold">
                                      {resource.materialName}
                                    </div>
                                    <div className=" text-sm text-brand-neutral-50 break-all">
                                      {resource.standardName}
                                    </div>
                                  </div>
                                  <div>{resource.useQuantity ?? 0}개</div>
                                </div>
                                <div
                                  onClick={() =>
                                    setAtgResource((prev) =>
                                      prev.filter(
                                        (item, currentIndex) =>
                                          currentIndex !== index
                                      )
                                    )
                                  }
                                >
                                  <ManagerReportDetailRemoveIcon />
                                </div>
                              </div>
                            </div>
                          ))}
                        </>
                      ) : (
                        <CommonTextArea
                          placeholder={
                            "투입 자재 정보를 기록해주세요\n예시) 노출콘텐츠 수량 2개"
                          }
                          value={resource}
                          onChange={(e) => {
                            setResource(e.target.value);
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="py-2.5 text-sm text-brand-neutral-60">
                    작업 전 사진
                  </div>
                  <CommonImageInput
                    maxImages={3}
                    images={beforeImages}
                    onChange={setBeforeImages}
                    thumbnailPosition="top"
                    setIsLoading={setIsLoading}
                    title={"작업 전 사진"}
                    type="workBefore"
                  />
                </div>

                <div>
                  <div className="py-2.5 text-sm text-brand-neutral-60">
                    작업 후 사진
                  </div>
                  <CommonImageInput
                    maxImages={3}
                    images={afterImages}
                    onChange={setAfterImages}
                    thumbnailPosition="top"
                    setIsLoading={setIsLoading}
                    title={"작업 후 사진"}
                    type="workAfter"
                  />
                </div>
              </div>
            </HistoryDetailBlock>

            <HistoryDetailBlock title="피드백">
              <div className="flex flex-col gap-1">
                <ManagerReportFeedbackRecommend
                  onChange={(text) => setFeedback(text)}
                  feedback={feedback}
                  editMode={editMode}
                />
              </div>
            </HistoryDetailBlock>
          </>
        )}

        {[
          REPORT_STATUS.COMPLETED.toString(),
          REPORT_STATUS.COMPLETED2.toString(),
        ].includes(reportDetail?.status ?? "") && (
          <>
            <HistoryDetailBlock title="작업/결과">
              <HistoryDetailRow title="작업자">
                {reportDetail?.worker || reportDetail?.completedByName}
              </HistoryDetailRow>
              {reportDetail?.resource && (
                <>
                  <HistoryDetailRow title="자재">
                    {JSON.parse(reportDetail.resource).map(
                      (resource: AtgResource, index: number) => (
                        <div className="flex gap-1" key={index}>
                          <div>{resource.materialName}</div>
                          <div>{resource.useQuantity}개</div>
                        </div>
                      )
                    )}
                  </HistoryDetailRow>
                </>
              )}

              {editMode ? (
                <div>
                  <div className="py-2.5 text-sm text-brand-neutral-60">
                    작업 전 사진
                  </div>
                  <CommonImageInput
                    maxImages={3}
                    images={beforeImages}
                    onChange={(images) => setBeforeImages(images)}
                    thumbnailPosition="top"
                    setIsLoading={setIsLoading}
                    title={"작업 전 사진"}
                    type="workBefore"
                  />
                </div>
              ) : reportDetail?.s3PreFile &&
                reportDetail.s3PreFile.length > 0 ? (
                <>
                  <HorizontalRule margin="0" />
                  <div className="py-1.5 flex flex-col">
                    <div className="py-3 text-sm text-brand-neutral-60 w-[90px] min-w-[90px]">
                      작업 전 사진
                    </div>
                    <div className="pb-2.5 gap-2.5 grid grid-cols-3">
                      {reportDetail.s3PreFile.map((file, index, fileList) => (
                        <ImagePreview
                          key={index}
                          image={file?.filePath}
                          imageList={fileList.map((item) => item.filePath)}
                          index={index}
                        />
                      ))}
                    </div>
                  </div>
                </>
              ) : null}

              {editMode ? (
                <div>
                  <div className="py-2.5 text-sm text-brand-neutral-60">
                    작업 후 사진
                  </div>
                  <CommonImageInput
                    maxImages={3}
                    images={afterImages}
                    onChange={(images) => setAfterImages(images)}
                    thumbnailPosition="top"
                    setIsLoading={setIsLoading}
                    title={"작업 후 사진"}
                    type="workAfter"
                  />
                </div>
              ) : (
                <>
                  {reportDetail?.s3PostFile?.length &&
                  reportDetail.s3PostFile.length > 0 ? (
                    <>
                      <HorizontalRule margin="0" />
                      <div className="py-1.5 flex flex-col">
                        <div className="py-3 text-sm text-brand-neutral-60 w-[90px] min-w-[90px]">
                          작업 후 사진
                        </div>
                        <div className="pb-2.5 gap-2.5 grid grid-cols-3">
                          {reportDetail.s3PostFile.map(
                            (file, index, fileList) => (
                              <ImagePreview
                                key={index}
                                image={file?.filePath}
                                imageList={fileList.map(
                                  (item) => item.filePath
                                )}
                                index={index}
                              />
                            )
                          )}
                        </div>
                      </div>
                    </>
                  ) : null}
                </>
              )}
            </HistoryDetailBlock>

            <HistoryDetailBlock title="피드백">
              <ManagerReportFeedbackRecommend
                feedback={feedback}
                onChange={(text) => setFeedback(text)}
                editMode={editMode}
              />
            </HistoryDetailBlock>
          </>
        )}

        {REPORT_STATUS.CANCELED.toString() === reportDetail?.status &&
          reportDetail?.canceledAt && (
            <HistoryDetailBlock title="취소 정보">
              <HistoryDetailRow title="취소 시간">
                {getYearToMinuteFromISODateString(
                  reportDetail?.canceledAt ?? ""
                )}
              </HistoryDetailRow>
            </HistoryDetailBlock>
          )}

        {getStatusBottomButton() && (
          <BottomGroup>{getStatusBottomButton()}</BottomGroup>
        )}
      </div>

      <CommonPopup
        isOpen={popup === "processConfirm"}
        padding={0}
        onClose={() => setPopup("")}
      >
        <div className="p-5 pt-0 text-xl font-medium flex items-center justify-center h-[110px]">
          접수확인 처리하시겠어요?
        </div>
        <div className="w-full flex">
          <CommonButton
            variant="secondary"
            className="rounded-none w-[100px] h-bottom-button"
            onClick={() => {
              setPopup("");
            }}
          >
            취소
          </CommonButton>
          <CommonButton
            className="rounded-none flex-1 h-bottom-button"
            onClick={async () => {
              setIsLoading(true);
              try {
                const res = await workingManagerReport({
                  centerIncommodityId: Number(params.id),
                });
                if (res.status === HttpStatusCode.Ok) {
                  await loadManagerReportDetail();
                  setPopup("");
                } else {
                  throw new Error();
                }
              } catch (e: any) {
                if (e?.response?.data?.error) {
                  open(e.response.data.error?.replace(". ", ".\n"));
                } else {
                  open("불편 신고 접수확인 처리에 실패했습니다.");
                }
              } finally {
                setIsLoading(false);
              }
            }}
          >
            확인
          </CommonButton>
        </div>
      </CommonPopup>

      <CommonPopup isOpen={popup === "scheduledConfirm"} padding={0}>
        <div className="p-5 pt-0 text-xl font-medium flex items-center justify-center h-[110px]">
          예정 처리하시겠어요?
        </div>
        <div className="w-full flex">
          <CommonButton
            variant="secondary"
            className="rounded-none w-[100px] h-bottom-button"
            onClick={() => {
              setPopup("");
            }}
          >
            취소
          </CommonButton>
          <CommonButton
            className="rounded-none flex-1 h-bottom-button"
            onClick={() => {
              setIsLoading(true);
              scheduleManagerReport({
                centerIncommodityId: Number(params.id),
                feedback,
              })
                .then((res) => {
                  if (res.status === HttpStatusCode.Ok) {
                    loadManagerReportDetail();
                    setPopup("");
                  }
                })
                .catch((reason) => {
                  if (reason?.response?.data?.error) {
                    open(reason.response.data.error?.replace(". ", ".\n"));
                  } else {
                    open("불편 신고 예정 처리에 실패했습니다.");
                  }
                })
                .finally(() => setIsLoading(false));
            }}
          >
            확인
          </CommonButton>
        </div>
      </CommonPopup>

      <CommonPopup isOpen={popup === "modifyConfirm"} padding={0}>
        <div className="p-5 pt-0 text-xl font-medium flex items-center justify-center h-[110px]">
          내용을 수정하시겠어요?
        </div>
        <div className="w-full flex">
          <CommonButton
            variant="secondary"
            className="rounded-none w-[100px] h-bottom-button"
            onClick={() => {
              setPopup("");
            }}
          >
            취소
          </CommonButton>
          <CommonButton
            className="rounded-none flex-1 h-bottom-button"
            onClick={async () => {
              if (params.id) {
                const formData = new FormData();

                const preFile = reportDetail?.s3PreFile ?? [];
                const postFile = reportDetail?.s3PostFile ?? [];

                let deletedBeforeImages: (S3File | ZadaraFile)[] = preFile;
                beforeImages.forEach((file) => {
                  if (typeof file === "string") {
                    deletedBeforeImages = deletedBeforeImages.filter(
                      (item) => item.filePath !== file
                    );
                  } else {
                    formData.append(`beforeFiles`, file);
                  }
                });

                let deletedAfterImages: (S3File | ZadaraFile)[] = postFile;
                afterImages.forEach((file) => {
                  if (typeof file === "string") {
                    deletedAfterImages = deletedAfterImages.filter(
                      (item) => item.filePath !== file
                    );
                  } else {
                    formData.append(`afterFiles`, file);
                  }
                });

                formData.append(
                  "centerIncommodityModifyDto",
                  JSON.stringify({
                    deleteFiles: [
                      ...deletedBeforeImages.map(
                        (item: any) => item?.["fileId"]
                      ),
                      ...deletedAfterImages.map(
                        (item: any) => item?.["fileId"]
                      ),
                    ],
                    feedback,
                  })
                );

                setIsLoading(true);
                try {
                  const result = await modifyManagerReport(
                    {
                      centerIncommodityId: Number(params.id),
                    },
                    formData
                  );
                  if (result.data.result === "E") {
                    setEditMode(false);
                    open(result.data.message);
                  } else {
                    await loadManagerReportDetail();
                  }
                } catch (e: any) {
                  if (e?.response?.data?.error) {
                    open(e.response.data.error?.replace(". ", ".\n"));
                  } else {
                    open("불편 신고 수정에 실패했습니다.");
                  }
                } finally {
                  setIsLoading(false);
                }
              }
              setPopup("");
            }}
          >
            확인
          </CommonButton>
        </div>
      </CommonPopup>

      <CommonPopup isOpen={popup === "completeConfirm"} padding={0}>
        <div className="p-5 pt-0 text-xl font-medium flex items-center justify-center h-[110px]">
          완료 처리하시겠어요?
        </div>
        <div className="w-full flex">
          <CommonButton
            variant="secondary"
            className="rounded-none w-[100px] h-bottom-button"
            onClick={() => {
              setPopup("");
            }}
          >
            취소
          </CommonButton>
          <CommonButton
            className="rounded-none flex-1 h-bottom-button"
            onClick={async () => {
              if (params.id) {
                const formData = new FormData();

                beforeImages.forEach((file, index) => {
                  formData.append(`beforeFiles`, file);
                });

                afterImages.forEach((file, index) => {
                  formData.append(`afterFiles`, file);
                });

                formData.append(
                  "CenterIncommodityProcessDto",
                  JSON.stringify({
                    feedback,
                    ...(reportDetail?.pushTypeCode?.includes("FACILITY") &&
                    reportDetail?.vocId
                      ? {
                          worker: atgWorker.map((worker) => worker.workerName),
                        }
                      : { workerString: worker }),
                    ...(reportDetail?.pushTypeCode?.includes("FACILITY") &&
                    reportDetail?.vocId
                      ? {
                          resource: atgResource,
                        }
                      : { resourceStr: resource }),
                    completedAt: formatFullDateTime(new Date()),
                  })
                );
                setIsLoading(true);
                try {
                  await completeManagerReport(
                    {
                      centerIncommodityId: Number(params.id),
                    },
                    formData
                  );
                  await loadManagerReportDetail();
                } catch (e: any) {
                  if (e?.response?.data?.error) {
                    open(e.response.data.error?.replace(". ", ".\n"));
                  } else {
                    open("불편 신고 완료 처리에 실패했습니다.");
                  }
                }
                setIsLoading(false);
              }
              setPopup("");
            }}
          >
            확인
          </CommonButton>
        </div>
      </CommonPopup>

      <CommonPopup
        isOpen={popup === "selectWorker"}
        padding={0}
        title="작업자 검색"
        onClose={() => setPopup("")}
      >
        <FindWorker
          atgBuildingId={reportDetail?.atgBuildingId ?? ""}
          onChange={(worker) => setAtgWorker(worker)}
          onClose={() => setPopup("")}
          prevSelectedWorker={atgWorker}
        />
      </CommonPopup>

      <CommonPopup
        isOpen={popup === "selectResource"}
        padding={0}
        title="자재 검색"
        onClose={() => setPopup("")}
      >
        <FindResource
          atgBuildingId={reportDetail?.atgBuildingId ?? ""}
          onChange={(resource) => setAtgResource(resource)}
          onClose={() => setPopup("")}
          prevSelectedResource={atgResource}
        />
      </CommonPopup>

      {simplePopupComponent}
      {virtualNumberComponent}
    </div>
  );
}

export const ManagerReportDetailCommentIcon = () => (
  <svg
    width="60"
    height="61"
    viewBox="0 0 60 61"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity="0.3">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M45.4093 46.2478C40.7911 50.8666 34.7121 53.1031 28.6712 52.9725C20.4675 52.7952 6.0791 52.924 6.0791 52.924C6.0791 52.924 6.20527 38.367 6.19907 30.0207C6.19471 24.1391 8.43411 18.2587 12.9294 13.7644C21.8902 4.80029 36.4486 4.80029 45.4093 13.762C54.3862 22.74 54.3701 37.2861 45.4093 46.2478Z"
        stroke="black"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M19.4767 31.0054H19.2187"
        stroke="black"
        strokeWidth="3"
        strokeLinecap="square"
      />
      <path
        d="M29.2355 31.0054H28.9775"
        stroke="black"
        strokeWidth="3"
        strokeLinecap="square"
      />
      <path
        d="M38.9943 31.0054H38.7363"
        stroke="black"
        strokeWidth="3"
        strokeLinecap="square"
      />
      <path
        d="M42.6981 23.9039C48.6086 23.9039 53.4 19.1125 53.4 13.202C53.4 7.29143 48.6086 2.5 42.6981 2.5C36.7875 2.5 31.9961 7.29143 31.9961 13.202C31.9961 19.1125 36.7875 23.9039 42.6981 23.9039Z"
        fill="black"
        stroke="white"
        strokeWidth="4"
      />
      <path
        d="M45.9508 9.94922L39.4453 16.4547"
        stroke="white"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
      <path
        d="M39.4453 9.94922L45.9508 16.4547"
        stroke="white"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

export const ManagerReportDetailRemoveIcon = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect y="0.5" width="24" height="24" rx="12" fill="#E7E7E8" />
    <g opacity="0.3">
      <path
        d="M16.5787 7.64941L7.37793 16.8502"
        stroke="black"
        strokeWidth="1.75"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
      <path
        d="M7.37793 7.64941L16.5787 16.8502"
        stroke="black"
        strokeWidth="1.75"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);
